import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Host, Inject, Input, NgModule, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { CssBackgroundImageProxyModule } from '@app/directives/css-background-image-proxy.directive';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { OrganizationEvent } from '@app/models/organization-events';
import { LinkModule } from '@app/directives/link.directive';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { OrganizationCalendarService } from '@app/services/organization-calendar.service';
import { Environment } from '@environments/interface.environment';
import { differenceInSeconds, isSameDay } from 'date-fns';
import { Subject, Subscription } from 'rxjs';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';

@Component({
  selector: 'app-organization-calendar',
  templateUrl: './organization-calendar.component.html',
  styleUrls: ['./organization-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrganizationCalendarComponent implements OnInit, OnDestroy {

  organizationEvents: OrganizationEvent[];
  allEvents: any[];
  eventsToDisplay: any[];
  groupedEvents: any;
  @Input() maxNumberOfShowMore = 2; // How may times can you press the show more button
  @Input() numEventsPerLoad = 6;  // 6 is a good number since it will work with all screen breakpoints
  maxEventsToShow: number;
  numEventsShowing: number;
  listGuid: string;
  calendarSiteUrl = this.featureSettingsStoreService.state.organizationCalenderSiteUrl ? this.featureSettingsStoreService.state.organizationCalenderSiteUrl : this.environment.coreWorkplaceSiteUrl;
  calendarUrl = `${this.calendarSiteUrl}/_layouts/15/Events.aspx`;
  singleEventCalendarUrl = `${this.calendarSiteUrl}/_layouts/15/Event.aspx`;
  loadEventSubscription: Subscription;
  private errorSubject$ = new Subject();
  numberOfShowMoreClicks = 0;
  calendarPageInfoUrl = this.featureSettingsStoreService.state.calendarPageInfoUrl;
  componentName =  'Upcoming events';
  eventsDisplayQuery = this.featureSettingsStoreService.state.eventsDisplayQuery;

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private service: OrganizationCalendarService,
              private router: Router,
              private componentEventService: ComponentStatesOnRoutesService,
              public elementRef: ElementRef,
              private cdRef: ChangeDetectorRef,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective,
              @Inject(DOCUMENT) private document: Document) {
  }

  clickShowMore(): void {
    this.numberOfShowMoreClicks++;
    this.numEventsShowing += this.numEventsPerLoad;
    if (this.numEventsShowing > this.maxEventsToShow) { this.numEventsShowing = this.maxEventsToShow; }
    this.eventsToDisplay = this.allEvents.slice(0, this.numEventsShowing);
  }

  clickShowLess(): void {
    this.numberOfShowMoreClicks = 0;
    this.maxEventsToShow = this.numEventsPerLoad * (this.maxNumberOfShowMore + 1);
    this.numEventsShowing = this.numEventsPerLoad;
    this.eventsToDisplay = this.allEvents.slice(0, this.numEventsPerLoad);
    this.scrollIntoFeatureView();
  }

  scrollIntoFeatureView() {
    const elementId = 'app-organization-calendar';
    const element = this.document.getElementById(elementId);

    if (element) {
      if (!/^(?:a|select|input|button|textarea)$/i.test(element.tagName)) {
        element.scrollIntoView();
      }
      element.focus();
    }
  }

  ngOnInit() {

    this.maxEventsToShow = this.numEventsPerLoad * (this.maxNumberOfShowMore + 1);
    this.numEventsShowing = this.numEventsPerLoad;

    const componentEvent = this.componentEventService.state.RouteMap.has(this.router.url) && this.componentEventService.state.RouteMap.get(this.router.url).find(x => x.ComponentId === this.elementRef.nativeElement.localName);
    const componentEventData = componentEvent ? componentEvent.Data as Array<OrganizationEvent> : undefined;

    this.loadEventSubscription = this.service.getOrganizationCalendarEventsFromGraph()
      .subscribe(events => {
        this.organizationEvents = events.map(val => {
          if (this.calendarUrl.indexOf('ListGuid') === -1) {
            this.calendarUrl = `${this.calendarUrl}?ListGuid=${val.ListGuid + this.eventsDisplayQuery}`;
          }
          return this.parseEvents(val);
        });

        let allEvents = [];
        for (let i = 0; i < this.organizationEvents.length; i++) {
          allEvents = allEvents.concat(this.parseRecurringEvents(this.organizationEvents[i]));
        }

        this.allEvents = this.orderByDate(allEvents, 'EventDate');

        this.allEvents = this.allEvents.map(event => {
          if (componentEventData) {
            const foundEvent = componentEventData.find(ced => ced.Id === event.Id);
            if (foundEvent) {
              event.ShowNewOrModifiedBadge = true;
            }
          } else {
            event.ShowNewOrModifiedBadge = false;
          }
          return event;
        });

        if (this.allEvents.length === 0) {
          this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
        } else {
          this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
        }

        // take top 5
        if (this.allEvents.length > this.numEventsPerLoad) {
          this.eventsToDisplay = this.allEvents.slice(0, this.numEventsPerLoad);
        } else {
          this.eventsToDisplay = this.allEvents;
        }
        this.cdRef.detectChanges();
      },
        err => {
          this.errorSubject$.next(err);
          this.cdRef.detectChanges();
        });
  }

  ngOnDestroy(): void {
    this.loadEventSubscription && this.loadEventSubscription.unsubscribe();
  }

  orderByDate(arr, dateProp) {
    return arr.slice().sort(function (a, b) {
      return a[dateProp] < b[dateProp] ? -1 : 1;
    });
  }

  parseEvents(event: OrganizationEvent): OrganizationEvent {

    let eventInstance;

    if (event.AllDayEvent) {
      eventInstance = '';
    } else {
      if (isSameDay(new Date(event.EventDate), new Date(event.EndDate))) {
        eventInstance = 'on same day';
      } else {
        eventInstance = 'on diff day';
      }
    }

    return new OrganizationEvent({
      EventDate: event.EventDate,
      EndDate: event.EndDate,
      EventInstance: eventInstance,
      Description: event.Description,
      AllDayEvent: event.AllDayEvent,
      Location: event.Location,
      Recurrence: event.Recurrence,
      Title: event.Title,
      RecurrenceData: event.RecurrenceData,
      Id: event.Id,
      ListGuid: event.ListGuid,
      OrganizationCalendarName: event.OrganizationCalendarName,
      BannerUrl: event.BannerUrl && event.BannerUrl !== 'https://none' ? event.BannerUrl.split('?')[0] : null,
      ItemUrl: `${this.singleEventCalendarUrl}?ListGuid=${event.ListGuid}&ItemId=${event.Id}`
    });
  }

  parseRecurringEvents(e: OrganizationEvent): OrganizationEvent[] {

    let start, end, durationInSec, eventInstance;
    const er = []; let total = 0; let rTotal = 0;
    const startDate = new Date(e.EventDate);
    const endDate = new Date(e.EndDate);

    durationInSec = differenceInSeconds(endDate, startDate); // (new Date(endDate).getHours() - new Date(startDate).getHours()) * 60 * 60;

    start = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
    end = new Date(this.parseDate(e.EndDate, e.AllDayEvent));

    if (isSameDay(new Date(start), new Date(end))) {
      eventInstance = 'on same day';
    } else {
      eventInstance = 'on diff day';
    }
    const url = `${this.singleEventCalendarUrl}?ListGuid=${e.ListGuid}&ItemId=${e.Id}`;
    // normal events on diff day
    if (!e.Recurrence) {
      if (eventInstance === 'on diff day') {
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        const ed = new Date(this.parseDate(e.EndDate, e.AllDayEvent));
        if ((new Date(init)).getTime() >= start.getTime()) {
          const ni = this.cloneObj(e);
          ni.EventDate = init;
          ni.EndDate = ed;
          ni.fRecurrence = false;
          ni.AllDayEvent = e.AllDayEvent;
          ni.EventInstance = eventInstance;
          ni.Id = e.Id;
          ni.ID = ni.Id;
          ni.itemUrl = url;
          if (new Date(ed) >= new Date()) {
            er.push(ni);
          }
        }
      } else {
        // normal events on same day
        e.EventDate = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        e.EndDate = new Date(this.parseDate(e.EndDate, e.AllDayEvent));
        e.AllDayEvent = e.AllDayEvent;
        e.EventInstance = eventInstance;
        e.ItemUrl = url;
        if (er.length <= 11) {
          er.push(e);
        } else { return; }
      }
      return er;
    } else {
      const wd = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
      const wom = ['first', 'second', 'third', 'fourth'];
      let rTotalstring = '';
      if (e.RecurrenceData.indexOf('<repeatInstances>') !== -1) {
        rTotalstring = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<repeatInstances>') + 17);
        rTotal = parseInt(rTotalstring.substring(0, rTotalstring.indexOf('<')), 10);
      }
      // daily recurrence expansion
      if (e.RecurrenceData.indexOf('<daily ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<daily '));
        str = str.substring(7, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        if (arr.indexOf('dayFrequency') !== -1) {
          const frequency = parseInt(arr[arr.indexOf('dayFrequency') + 1], 10);
          let loop = true;
          const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
          while (loop) {
            total++;
            if ((new Date(init)).getTime() >= start.getTime()) {

              const ed = new Date(init);
              ed.setSeconds(ed.getSeconds() + durationInSec);
              const ni = this.cloneObj(e);
              ni.EventDate = new Date(init);
              ni.EndDate = ed;
              ni.fRecurrence = false;
              ni.AllDayEvent = e.AllDayEvent;
              ni.EventInstance = 'on same day';
              ni.Id = e.Id;
              ni.ID = ni.Id;
              ni.ItemUrl = url;
              if (new Date(ni.EventDate) >= new Date()) {
                if (er.length <= 11) {
                  er.push(ni);
                } else { break; }
              }
            }
            init.setDate(init.getDate() + frequency);
            if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
          }
        } else if (arr.indexOf('weekday') !== -1) {
          e.RecurrenceData = e.RecurrenceData + '<weekly mo=\'TRUE\' tu=\'TRUE\' we=\'TRUE\' th=\'TRUE\' fr=\'TRUE\' weekFrequency=\'1\' />'; // change from daily on every weekday to weekly on every weekday
        }
      }

      // weekly recurrence expansion
      if (e.RecurrenceData.indexOf('<weekly ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<weekly '));
        str = str.substring(8, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        const frequency = parseInt(arr[arr.indexOf('weekFrequency') + 1], 10);
        let loop = true;
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        let initDay = new Date(init).getDay();
        while (loop) {
          for (let i = initDay; i < 7; i++) {
            if (arr.indexOf(wd[i]) !== -1 && (rTotal > total || rTotal === 0)) {
              total++;
              if ((new Date(init)).getTime() >= start.getTime()) {

                const nd = new Date(init);
                nd.setDate(nd.getDate() + (i - initDay));
                const ed = new Date(nd);
                ed.setSeconds(ed.getSeconds() + durationInSec);
                const ni = this.cloneObj(e);
                ni.EventDate = new Date(nd);
                ni.EndDate = ed;
                ni.fRecurrence = false;
                ni.AllDayEvent = e.AllDayEvent;
                ni.EventInstance = 'on same day';
                ni.Id = e.Id;
                ni.ID = ni.Id;
                ni.ItemUrl = url;
                if (new Date(ni.EventDate) >= new Date()) {
                  if (er.length <= 11) {
                    er.push(ni);
                  } else { break; }
                }
              }
            }
          }
          init.setDate(init.getDate() + ((7 * frequency) - initDay));
          initDay = 0;
          if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
        }
      }

      // monthly recurrence expansion
      if (e.RecurrenceData.indexOf('<monthly ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<monthly '));
        str = str.substring(9, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        const frequency = parseInt(arr[arr.indexOf('monthFrequency') + 1], 10);
        let loop = true;
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        const day = parseInt(arr[arr.indexOf('day') + 1], 10);
        while (loop) {
          total++;
          if ((new Date(init)).getTime() >= start.getTime()) {
            const nd = new Date(init);
            nd.setDate(day);
            if (nd.getMonth() === init.getMonth()) {

              const ed = new Date(nd);
              ed.setSeconds(ed.getSeconds() + durationInSec);
              const ni = this.cloneObj(e);
              ni.EventDate = new Date(nd);
              ni.EndDate = ed;
              ni.fRecurrence = false;
              ni.AllDayEvent = e.AllDayEvent;
              ni.EventInstance = 'on same day';
              ni.Id = e.Id;
              ni.ID = ni.Id;
              ni.ItemUrl = url;
              if (new Date(ni.EventDate) >= new Date()) {
                if (er.length <= 11) {
                  er.push(ni);
                } else { break; }
              }
            }
          }
          init.setMonth(init.getMonth() + frequency);
          if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
        }
      }

      // monthlyByDay recurrence expansion
      if (e.RecurrenceData.indexOf('<monthlyByDay ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<monthlyByDay '));
        str = str.substring(14, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        const frequency = parseInt(arr[arr.indexOf('monthFrequency') + 1], 10);
        let loop = true;
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        const weekdayOfMonth = arr[arr.indexOf('weekdayOfMonth') + 1];
        let temp = new Date();
        while (loop) {
          total++;
          if ((new Date(init)).getTime() >= start.getTime()) {
            let nd = new Date(init);
            nd.setDate(1); // set to first day of month
            if (arr.indexOf('weekday') !== -1) { // find first weekday - if not saturday or sunday, then current date is a weekday
              if (nd.getDay() === 0) { nd.setDate(nd.getDate() + 1); } else if (nd.getDay() === 6) { nd.setDate(nd.getDate() + 2); } // add two days to saturday
              if (weekdayOfMonth === 'last') {
                while (nd.getMonth() === init.getMonth()) {
                  temp = new Date(nd);
                  if (nd.getDay() === 5) { nd.setDate(nd.getDate() + 3); } else { nd.setDate(nd.getDate() + 1); } // otherwise, just add one day
                }
                nd = new Date(temp);
              } else {
                for (let i = 0; i < wom.indexOf(weekdayOfMonth); i++) {
                  if (nd.getDay() === 5) { nd.setDate(nd.getDate() + 3); } else { nd.setDate(nd.getDate() + 1); } // otherwise, just add one day
                }
              }
            } else if (arr.indexOf('weekend_day') !== -1) { // find first weekend day
              if (nd.getDay() !== 0 && nd.getDay() !== 6) { nd.setDate(nd.getDate() + (6 - nd.getDay())); } // if not saturday or sunday, then add days to get to saturday
              if (weekdayOfMonth === 'last') {
                while (nd.getMonth() === init.getMonth()) {
                  temp = new Date(nd);
                  if (nd.getDay() === 0) { nd.setDate(nd.getDate() + 6); } else { nd.setDate(nd.getDate() + 1); } // otherwise, just add one day
                }
                nd = new Date(temp);
              } else {
                for (let i = 0; i < wom.indexOf(weekdayOfMonth); i++) {
                  if (nd.getDay() === 0) { nd.setDate(nd.getDate() + 6); } else { nd.setDate(nd.getDate() + 1); } // otherwise, just add one day
                }
              }
            } else if (arr.indexOf('day') !== -1) {// just looking for the Nth day in the month...
              if (weekdayOfMonth === 'last') {
                nd.setMonth(nd.getMonth() + 1);
                nd.setDate(0);
              } else { nd.setDate(nd.getDate() + (wom.indexOf(weekdayOfMonth))); } // now add days to get to the Nth instance of this day
            } else {
              for (let i = 0; i < wd.length; i++) { // get first instance of the specified day
                if (arr.indexOf(wd[i]) !== -1) {
                  if (nd.getDate() > i) { nd.setDate(nd.getDate() + (7 - (nd.getDay() - i))); } else { nd.setDate(nd.getDate() + (i - nd.getDay())); }
                }
              }
              if (weekdayOfMonth === 'last') {
                while (nd.getMonth() === init.getMonth()) {
                  temp = new Date(nd);
                  nd.setDate(nd.getDate() + 7); // add a week to each instance to get the Nth instance
                }
                nd = new Date(temp);
              } else {
                for (let i = 0; i < wom.indexOf(weekdayOfMonth); i++) {
                  nd.setDate(nd.getDate() + 7); // add a week to each instance to get the Nth instance
                }
              }
            }
            if (nd.getMonth() === init.getMonth()) { // make sure the new date calculated actually falls within the current month (sometimes there may be no 4th instance of a day)

              const ed = new Date(nd);
              ed.setSeconds(ed.getSeconds() + durationInSec);
              const ni = this.cloneObj(e);
              ni.EventDate = new Date(nd);
              ni.EndDate = ed;
              ni.fRecurrence = false;
              ni.AllDayEvent = e.AllDayEvent;
              ni.EventInstance = 'on same day';
              ni.Id = e.Id;
              ni.ID = ni.Id;
              ni.ItemUrl = url;
              if (new Date(ni.EventDate) >= new Date()) {
                if (er.length <= 11) {
                  er.push(ni);
                } else { break; }
              }
            }
          }
          init.setMonth(init.getMonth() + frequency);
          if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
        }
      }

      // yearly recurrence expansion
      if (e.RecurrenceData.indexOf('<yearly ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<yearly '));
        str = str.substring(8, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        const frequency = parseInt(arr[arr.indexOf('yearFrequency') + 1], 10);
        let loop = true;
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        const month = (parseInt(arr[arr.indexOf('month') + 1], 10) - 1);
        const day = parseInt(arr[arr.indexOf('day') + 1], 10);
        while (loop) {
          const nd = new Date(init);
          nd.setMonth(month);
          nd.setDate(day);
          if ((new Date(init)).getTime() >= nd.getTime()) {
            total++;
            if ((new Date(init)).getTime() >= start.getTime()) {

              const ed = new Date(nd);
              ed.setSeconds(ed.getSeconds() + durationInSec);
              const ni = this.cloneObj(e);
              ni.EventDate = new Date(nd);
              ni.EndDate = ed;
              ni.fRecurrence = false;
              ni.AllDayEvent = e.AllDayEvent;
              ni.EventInstance = 'on same day';
              ni.Id = e.Id;
              ni.ID = ni.Id;
              ni.ItemUrl = url;
              if (new Date(ni.EventDate) >= new Date()) {
                if (er.length <= 11) {
                  er.push(ni);
                } else { break; }
              }
            }
          }
          init.setFullYear(init.getFullYear() + frequency);
          if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
        }
      }

      // yearlyByDay recurrence expansion
      if (e.RecurrenceData.indexOf('<yearlyByDay ') !== -1) {
        let str = e.RecurrenceData.substring(e.RecurrenceData.indexOf('<yearlyByDay '));
        str = str.substring(13, str.indexOf('/>') - 1);
        const arr = this.formatString(str);
        const frequency = parseInt(arr[arr.indexOf('yearFrequency') + 1], 10);
        let loop = true;
        const init = new Date(this.parseDate(e.EventDate, e.AllDayEvent));
        const month = (parseInt(arr[arr.indexOf('month') + 1], 10) - 1);
        const weekdayOfMonth = arr[arr.indexOf('weekdayOfMonth') + 1];
        let day = 0;
        for (let i = 0; i < wd.length; i++) {
          if (arr.indexOf(wd[i]) !== -1) {
            if (arr[arr.indexOf(wd[i]) + 1].toLowerCase() === 'true') { day = i; }
          }
        }
        while (loop) {
          let nd = new Date(init);
          nd.setMonth(month);
          if ((new Date(init)).getTime() >= nd.getTime()) {
            total++;
            if ((new Date(init)).getTime() >= start.getTime()) {
              nd.setDate(1);
              const dayOfMonth = nd.getDay();
              if (day < dayOfMonth) { nd.setDate(nd.getDate() + ((7 - dayOfMonth) + day)); } else { nd.setDate(nd.getDate() + (day - dayOfMonth)); }
              if (weekdayOfMonth === 'last') {
                const temp = new Date(nd);
                while (temp.getMonth() === month) {
                  nd = new Date(temp);
                  temp.setDate(temp.getDate() + 7); // loop from first instance of month to last instance of month
                }
              } else {
                nd.setDate(nd.getDate() + (7 * (wom.indexOf(weekdayOfMonth))));
              }
              if (nd.getMonth() === month) {

                const ed = new Date(nd);
                ed.setSeconds(ed.getSeconds() + durationInSec);
                const ni = this.cloneObj(e);
                ni.EventDate = new Date(nd);
                ni.EndDate = ed;
                ni.fRecurrence = false;
                ni.AllDayEvent = e.AllDayEvent;
                ni.EventInstance = 'on same day';
                ni.Id = e.Id;
                ni.ID = ni.Id;
                ni.ItemUrl = url;
                if (new Date(ni.EventDate) >= new Date()) {
                  if (er.length <= 11) {
                    er.push(ni);
                  } else { break; }
                }
              }
            }
          }
          init.setFullYear(init.getFullYear() + frequency);
          init.setMonth(month);
          init.setDate(1);
          if ((new Date(init) > end) || (rTotal > 0 && rTotal <= total)) { loop = false; }
        }
      }

      return er;
    } // end recurrence check
  }

  parseDate(date, allDay): Date {
    if (typeof date === 'string') {
      if (allDay) {
        if (date.lastIndexOf('Z') === date.length - 1) {
          const dt = date.substring(0, date.length - 1);
          return new Date(dt);
        }
      } else {
        return new Date(date);
      }
    }
    return date;
  }

  formatString(str: string) {
    let arr = str.split('\'');
    str = arr.join('');
    arr = str.split('"');
    str = arr.join('');
    arr = str.split('=');
    str = arr.join(' ');
    str.trim();
    return str.split(' ');
  }

  cloneObj(obj: any) {
    let copy;
    if (null === obj || 'object' !== typeof obj) { return obj; }
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.cloneObj(obj[i]);
      }
      return copy;
    }
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) { copy[attr] = this.cloneObj(obj[attr]); }
      }
      return copy;
    }
    throw new Error('Unable to copy obj! Its type isn\'t supported.');
  }

  setCalendarStyle(event: OrganizationEvent) {
    let cssClass: string;

    if (!event.BannerUrl || event.BannerUrl === 'https://none') {
      cssClass = 'calendar-box-background';
    } else {
      cssClass = 'calendar-image-box-font-color';
    }
    return cssClass;
  }

  setLayOverStyle(event: OrganizationEvent) {
    let cssClass = {};
    if (event.BannerUrl) {
      cssClass = 'calendar-banner-overlay';
    }
    return cssClass;
  }

}
@NgModule({
  declarations: [OrganizationCalendarComponent],
  exports: [OrganizationCalendarComponent],
  imports: [
    CommonModule,
    ActionLinkModule,
    MatButtonModule,
    CssBackgroundImageProxyModule,
    ErrorTemplatesModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule,
    LinkModule,
  ]
})
export class OrganizationCalendarModule { }
